// Ref: https://github.com/plaid/react-plaid-link/blob/master/examples/oauth.tsx

import { showInstitutionAlreadyConnectedModal } from '@/components/financialEntities/InstitutionAlreadyConnectedModal';
import { useTracking } from '@/services/analytics/useTracking';
import { showConfetti } from '@/util/confetti';
import { useMutation, useQuery } from '@apollo/client';
import { assert } from '@common/errors';
import {
    CompletePlaidRelinkDocument,
    ExchangePlaidPublicTokenDocument,
    PlaidLinkDocument,
} from '@common/graphql/generated';
import { UsePlaidOptions } from '@common/react/aggregators';
import { useCallback, useEffect } from 'react';
import {
    PlaidLinkOnEvent,
    PlaidLinkOnExit,
    PlaidLinkOnSuccess,
    PlaidLinkOptions,
    usePlaidLink,
} from 'react-plaid-link';
import useLocalStorageState from 'use-local-storage-state';
import { useGlobalTrapFocus } from '../common/GlobalTrapFocusContext';
import { showAccountLinkingConfirmationModal } from '../financialEntities/AccountLinkingConfirmationModal';
import {
    hideAccountLinkingInProgressModal,
    showAccountLinkingInProgressModal,
} from '../financialEntities/AccountLinkingInProgressModal';

const usePlaid = (
    options?: UsePlaidOptions
): {
    open: () => void;
    ready: boolean;
} => {
    const { setTrapFocus } = useGlobalTrapFocus();
    const { track } = useTracking();
    const {
        financialEntity,
        onComplete,
        confirmationNextButtonText,
        enableAccountSelection,
    } = options ?? {};

    const [token, setToken, { removeItem: removeTokenFromLocalStorage }] =
        useLocalStorageState<string>(`plaidLinkToken-${financialEntity?.id}`);
    const [
        isUpdateMode,
        setIsUpdateMode,
        { removeItem: removeIsUpdateModeFromLocalStorage },
    ] = useLocalStorageState<boolean>(
        `plaidLinkIsUpdateMode-${financialEntity?.id}`,
        {
            defaultValue: false,
        }
    );
    const isOAuthRedirect =
        globalThis.location?.href?.includes('?oauth_state_id=');
    const { data, refetch } = useQuery(PlaidLinkDocument, {
        // If page is handling an OAuth redirect, do not generate a new token.
        // Instead, use the previously generated token from localStorage.
        // https://plaid.com/docs/link/oauth/#reinitializing-link
        skip: isOAuthRedirect,
        fetchPolicy: 'no-cache',
        ...(financialEntity && {
            variables: {
                financialEntityId: financialEntity.id,
                enableAccountSelection: !!enableAccountSelection,
            },
        }),
    });

    const [exchangePlaidPublicToken] = useMutation(
        ExchangePlaidPublicTokenDocument
    );
    const [completePlaidRelink] = useMutation(CompletePlaidRelinkDocument);

    const finish = useCallback(() => {
        setTrapFocus(true);
    }, [setTrapFocus]);

    // When a new link token is received, update local state and localStorage.
    useEffect(() => {
        if (data?.plaidLink) {
            setToken(data.plaidLink.linkToken);
            setIsUpdateMode(data.plaidLink.isUpdateMode);
        }
    }, [data, setToken, setIsUpdateMode]);

    const logEvent = useCallback(
        (eventName: string, data: Record<string, unknown>) => {
            track(`Plaid Link - ${eventName}`, data);
        },
        [track]
    );

    const onSuccess = useCallback<PlaidLinkOnSuccess>(
        async (publicToken, metadata) => {
            finish();
            logEvent('onSuccess', { publicToken, metadata, isUpdateMode });
            removeTokenFromLocalStorage();
            removeIsUpdateModeFromLocalStorage();

            if (isUpdateMode) {
                assert(financialEntity);
                await completePlaidRelink({
                    variables: { financialEntityId: financialEntity.id },
                });
                onComplete?.();
            } else {
                showAccountLinkingInProgressModal({
                    institutionName: metadata.institution?.name,
                    institutionId: metadata.institution?.institution_id,
                });

                // Send public token to the server, which will then exchange it for an access token.
                // https://plaid.com/docs/api/tokens/#token-exchange-flow
                const { data } = await exchangePlaidPublicToken({
                    variables: {
                        publicToken,
                        metadata,
                        ...(financialEntity && {
                            linkTo: {
                                id: financialEntity.id,
                                type: financialEntity.type,
                            },
                        }),
                    },
                });

                if (!data?.exchangePlaidPublicToken) {
                    console.warn('No data from exchangePlaidPublicToken');
                    hideAccountLinkingInProgressModal();
                    return;
                }

                if (data.exchangePlaidPublicToken.existingInstitutionName) {
                    track('Institution Already Connected Error', {
                        institution_name:
                            data.exchangePlaidPublicToken
                                .existingInstitutionName,
                    });
                    onComplete?.();
                    hideAccountLinkingInProgressModal();
                    showInstitutionAlreadyConnectedModal({
                        institutionName:
                            data.exchangePlaidPublicToken
                                .existingInstitutionName,
                    });
                } else {
                    assert(
                        data.exchangePlaidPublicToken
                            .confirmedFinancialEntityIds
                    );
                    assert(
                        data.exchangePlaidPublicToken
                            .unconfirmedFinancialEntityIds
                    );

                    const {
                        unconfirmedFinancialEntityIds,
                        confirmedFinancialEntityIds,
                    } = data.exchangePlaidPublicToken;

                    hideAccountLinkingInProgressModal();

                    if (
                        unconfirmedFinancialEntityIds.length === 0 &&
                        confirmedFinancialEntityIds.length > 0
                    ) {
                        // Don't show confirmation modal if all accounts are already confirmed
                        showConfetti();
                    } else {
                        showAccountLinkingConfirmationModal({
                            financialEntityIds: unconfirmedFinancialEntityIds,
                            nextButtonText: confirmationNextButtonText,
                            onComplete,
                        });
                    }

                    refetch();
                }
            }
        },
        [
            confirmationNextButtonText,
            exchangePlaidPublicToken,
            finish,
            financialEntity,
            logEvent,
            completePlaidRelink,
            isUpdateMode,
            removeIsUpdateModeFromLocalStorage,
            removeTokenFromLocalStorage,
            onComplete,
            refetch,
            track,
        ]
    );

    const onEvent = useCallback<PlaidLinkOnEvent>(
        (eventName, metadata) => {
            // log onEvent callbacks from Link
            // https://plaid.com/docs/link/web/#onevent
            logEvent(eventName, { metadata });
        },
        [logEvent]
    );

    const onExit = useCallback<PlaidLinkOnExit>(
        (error, metadata) => {
            finish();
            // log onExit callbacks from Link, handle errors
            // https://plaid.com/docs/link/web/#onexit
            logEvent('onExit', { error, metadata });
        },
        [logEvent, finish]
    );

    const config: PlaidLinkOptions = {
        // token must be the same token used for the first initialization of Link
        token: token ?? null,
        onSuccess,
        onEvent,
        onExit,
    };

    if (isOAuthRedirect) {
        // receivedRedirectUri must include the query params
        config.receivedRedirectUri = window.location.href;
    }

    const {
        open,
        ready,
        // error,
        // exit
    } = usePlaidLink(config);

    const onOpen = useCallback(() => {
        // Chakra's trapFocus interferes with the Plaid iframe's inputs. To
        // address this, we disable trapFocus for all Chakra interstitials
        // (Drawer & Modal) while the Plaid flow is active, then re-enable
        // trapFocus when it finishes (see `finish()`, above).
        setTrapFocus(false);
        open();
    }, [setTrapFocus, open]);

    useEffect(() => {
        // If OAuth redirect, instantly open link when it is ready instead of
        // making user click the button
        if (isOAuthRedirect && ready) {
            onOpen();
        }
    }, [ready, onOpen, isOAuthRedirect]);

    return {
        open: onOpen,
        ready,
    };
};

export default usePlaid;
